import { Location, Station } from "@today/api/tracker"
import { sleepAsync } from "@today/lib"
import { TODAY_INDIGO } from "@today/ui"
import axios from "axios"
import Script from "next/script"
import { useEffect, useState } from "react"
import ReactDOMServer from "react-dom/server"
import { MapBubble } from "."
import LoadIcon from "../../public/images/markers/load.svg"
import type { RouteCoordinatesResponse } from "../../pages/api/routes/from/[fromStationId]/to/[toStationId]"

type MapProps = {
  steps: {
    station: Station
    arrivalTime: string | null
    departureTime: string | null
  }[]
  holdingDelivery: boolean
  delivered: boolean
  senderLocation?: Location
  receiverLocation: Location
  listeners?: {
    onClick?(): void
    onDoubleClick?(): void
    onPinchEnd?(): void
    onDragEnd?(): void
  }
}

export function Map({
  steps,
  holdingDelivery,
  delivered,
  senderLocation,
  receiverLocation,
  listeners,
}: MapProps) {
  const [isMapLoaded, setMapLoaded] = useState(false)
  useEffect(() => {
    if (typeof naver === "undefined") return
    const map = new naver.maps.Map("map", {
      mapDataControl: false,
      scaleControl: false,
    })

    // 디자인 상, 지도 하단에 다른 버튼들이 있기에 bottom에 추가적인 margin을 넣어줌.
    const fit = (locations: Location[]) => {
      const { southWest, northEast } = getEnclosingBounds(locations)
      map.fitBounds(
        new naver.maps.LatLngBounds(
          toNaverLatLng(southWest),
          toNaverLatLng(northEast)
        ),
        { top: 15 + 10 + 115, right: 15, bottom: 15 + 95, left: 15 }
      )
    }

    // Add listeners
    naver.maps.Event.addListener(map, "click", () => listeners?.onClick?.())
    naver.maps.Event.addListener(map, "dblclick", () =>
      listeners?.onDoubleClick?.()
    )
    naver.maps.Event.addListener(map, "pinchend", () =>
      listeners?.onPinchEnd?.()
    )
    naver.maps.Event.addListener(map, "dragend", () => listeners?.onDragEnd?.())

    // 거쳐 간 정류소가 없을 수 있다 (위탁 인계 뒤 이력이 없는 건). 배열을 인덱스로
    // 바로 읽으면 그때 화면 전체가 죽으므로, 있을 때만 쓰도록 먼저 꺼내 둔다.
    const firstStation = steps[0]?.station.location
    const lastStation = steps[steps.length - 1]?.station.location

    // 정류소 마커
    steps.forEach(({ station, arrivalTime, departureTime }, index) => {
      const isPickUp = station.level === "PU"
      const isLastStation = index === steps.length - 1
      const atStation = !!arrivalTime && !departureTime
      new naver.maps.Marker({
        map,
        position: toNaverLatLng(station.location),
        icon: {
          content: ReactDOMServer.renderToStaticMarkup(
            isPickUp ? (
              <div className="flex w-[100px] flex-col items-center">
                <MapBubble color="BLUE" text="출발지" />
                <div className="border-blue rounded-full border-4 bg-white shadow-md">
                  <div className="h-3 w-3" />
                </div>
              </div>
            ) : (
              <div className="flex w-[100px] flex-col items-center">
                <div className={atStation ? "visible" : "invisible"}>
                  <MapBubble
                    color={holdingDelivery ? "INDIGO" : "GREEN"}
                    text={
                      holdingDelivery
                        ? "배송 보류"
                        : isLastStation
                        ? "곧 출발 예정"
                        : "이동 대기중"
                    }
                  />
                </div>
                <div
                  className={`rounded-full border-4 bg-white shadow-md ${
                    atStation
                      ? "border-green"
                      : arrivalTime
                      ? "border-indigo"
                      : "border-gray-3"
                  }`}
                >
                  {atStation ? <LoadIcon /> : <div className="h-3 w-3" />}
                </div>
                <div
                  className="mt-0.5 text-xs font-black"
                  style={{
                    textShadow:
                      "-2px 0 white, 0 2px white, 2px 0 white, 0 -2px white",
                  }}
                >
                  {station.name}
                </div>
              </div>
            )
          ),
          anchor: { x: 50, y: atStation ? 50 : 44 },
        },
      })
    })

    // 도착지 마커
    new naver.maps.Marker({
      map,
      position: toNaverLatLng(receiverLocation),
      icon: {
        content: ReactDOMServer.renderToStaticMarkup(
          <div className="flex w-[100px] flex-col items-center">
            {delivered ? (
              <MapBubble color="RED" text="배송완료" />
            ) : (
              <MapBubble color="GRAY" text="도착지" />
            )}
            <div
              className={`rounded-full border-4 bg-white shadow-md ${
                delivered ? "border-red" : "border-gray-3"
              }`}
            >
              {delivered ? <LoadIcon /> : <div className="h-3 w-3" />}
            </div>
          </div>
        ),
        anchor: { x: 50, y: delivered ? 50 : 44 },
      },
    })

    // 광역 정류소 - 지역 정류소 실선
    for (let index = 0; index < steps.length - 1; index++) {
      const from = steps[index]
      const to = steps[index + 1]
      drawRouteBetweenStations(
        map,
        from.station,
        to.station,
        to.arrivalTime ? "FILLED" : from.departureTime ? "HALF" : "EMPTY"
      )
    }

    // 출발지 마커, 출발지 - 픽업지 점선
    if (senderLocation) {
      new naver.maps.Marker({
        map,
        position: toNaverLatLng(senderLocation),
        icon: {
          content: ReactDOMServer.renderToStaticMarkup(
            <div className="flex w-[100px] flex-col items-center">
              <MapBubble color="BLUE" text="출발지" />
              <div className="border-blue rounded-full border-4 bg-white shadow-md">
                <div className="h-3 w-3" />
              </div>
            </div>
          ),
          anchor: { x: 50, y: 44 },
        },
      })

      // 거쳐 간 정류소가 하나도 없으면 출발지에서 도착지로 곧장 잇는다. 위탁 건은
      // 인계 뒤 정류소 이력이 남지 않아 이 배열이 빌 수 있다.
      new naver.maps.Polyline({
        map,
        path: [
          toNaverLatLng(senderLocation),
          toNaverLatLng(firstStation ?? receiverLocation),
        ],
        strokeWeight: 2,
        strokeColor: TODAY_INDIGO,
        strokeStyle: "shortdash",
      })
    }

    // 마지막 정류소 - 도착지 점선
    if (delivered && lastStation) {
      new naver.maps.Polyline({
        map,
        path: [toNaverLatLng(lastStation), toNaverLatLng(receiverLocation)],
        strokeWeight: 2,
        strokeColor: TODAY_INDIGO,
        strokeStyle: "shortdash",
      })
    }

    const zoomInAndOut = async () => {
      await sleepAsync(100)
      map.zoomBy(1)
      await sleepAsync(10)
      map.zoomBy(-1)
    }
    zoomInAndOut()

    const reversedSteps = [...steps].reverse()
    const lastStep = reversedSteps[0]
    const arrivedIndex = reversedSteps.findIndex((step) => !!step.arrivalTime)
    const currentIndex = arrivedIndex >= 0 ? steps.length - 1 - arrivedIndex : 0
    if (!lastStep) {
      // 정류소가 없으면 출발지·도착지만으로 지도를 맞춘다. 그냥 돌아가면 지도가 기본
      // 위치(서울 한복판)에 머물러 물품과 상관없는 곳을 보여 준다.
      fit(
        senderLocation ? [senderLocation, receiverLocation] : [receiverLocation]
      )
      return
    }
    const arrivedAtLastStation = !!lastStep.arrivalTime
    const inDelivery = !!lastStep.departureTime && !delivered

    // Fit
    fit(
      delivered || currentIndex === 0
        ? [...steps.map((step) => step.station.location), receiverLocation]
        : arrivedAtLastStation || steps.length === 1
        ? [lastStep.station.location, receiverLocation]
        : steps[currentIndex]
        ? [
            steps[currentIndex].station.location,
            // 다음 정류소가 없으면 도착지로 맞춘다. 위 조건들에 걸려 여기까지 오지
            // 않지만, 한 칸이라도 비면 화면 전체가 죽는 자리라 값을 비워 두지 않는다.
            steps[currentIndex + 1]?.station.location ?? receiverLocation,
          ]
        : [
            steps[0].station.location,
            steps[1]?.station.location ?? receiverLocation,
          ]
    )
  }, [isMapLoaded, receiverLocation, steps, delivered, holdingDelivery])
  return (
    <div id="map" className="h-full w-full">
      <Script
        src="https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=i4t1g3zxwo"
        onLoad={() => setMapLoaded(true)}
      />
    </div>
  )
}

function getEnclosingBounds(locations: Location[]): {
  southWest: Location
  northEast: Location
} {
  const latitudes = locations.map((location) => location.latitude)
  const longitudes = locations.map((location) => location.longitude)
  return {
    southWest: {
      latitude: Math.min(...latitudes),
      longitude: Math.min(...longitudes),
    },
    northEast: {
      latitude: Math.max(...latitudes),
      longitude: Math.max(...longitudes),
    },
  }
}

function toNaverLatLng(location: Location): naver.maps.LatLng {
  return new naver.maps.LatLng(location.latitude, location.longitude)
}

async function drawRouteBetweenStations(
  map: naver.maps.Map,
  from: Station,
  to: Station,
  type: "FILLED" | "HALF" | "EMPTY"
) {
  const { data } = await axios
    .get<RouteCoordinatesResponse>(`/api/routes/from/${from.id}/to/${to.id}`)
    .catch(() => ({
      data: {
        coordinates: [],
      } as RouteCoordinatesResponse,
    }))
  const { coordinates, reversed } = data
  const route = [
    from.location,
    ...(reversed ? coordinates.reverse() : coordinates).map(
      (coords: number[]) => {
        const longitude = coords[0]
        const latitude = coords[1]
        return { latitude, longitude }
      }
    ),
    to.location,
  ]
  switch (type) {
    case "FILLED":
      new naver.maps.Polyline({
        map,
        path: route.map((location) => toNaverLatLng(location)),
        strokeWeight: 4,
        strokeColor: "#081236",
      })
      break
    case "EMPTY":
      new naver.maps.Polyline({
        map,
        path: route.map((location) => toNaverLatLng(location)),
        strokeWeight: 4,
        strokeColor: "#8A8A8A",
      })
      break
    case "HALF":
      const middleIndex = Math.floor(route.length / 2)
      const middle = route[middleIndex]
      new naver.maps.Polyline({
        map,
        path: route
          .slice(0, middleIndex + 1)
          .map((location) => toNaverLatLng(location)),
        strokeWeight: 4,
        strokeColor: "#081236",
      })
      new naver.maps.Polyline({
        map,
        path: route
          .slice(middleIndex)
          .map((location) => toNaverLatLng(location)),
        strokeWeight: 4,
        strokeColor: "#8A8A8A",
      })
      new naver.maps.Marker({
        map,
        position: toNaverLatLng(middle),
        icon: {
          content: ReactDOMServer.renderToStaticMarkup(
            <div className="flex w-[100px] flex-col items-center">
              <MapBubble color="GREEN" text="이동중" />
              {/* 네이버 지도 마커용 정적 마크업이라 next/image 사용 불가 */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/markers/deli.png"
                width={36}
                height={36}
                alt=""
              />
            </div>
          ),
          anchor: { x: 50, y: 50 },
        },
      })
      break
  }
}
